import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, Linkedin, Twitter, X } from "lucide-react";

const SOCIALS = [
  { label: "Twitter / X",  href: "https://x.com/Malicke_msb1000",                   Icon: Twitter,  color: "#1DA1F2" },
  { label: "YouTube",      href: "https://www.youtube.com/@malickecase",            Icon: Youtube,  color: "#FF0000" },
  { label: "LinkedIn",     href: "https://linkedin.com/in/malicke-case-41a788177",   Icon: Linkedin, color: "#0A66C2" },
];

/* Forma runs as its own Cloudflare Worker on this subdomain. */
const FORMA_URL = "https://forma.malickecase.com";

/* Shared card styles */
const cardBase = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  borderRadius: 16,
  padding: "22px 20px",
  textDecoration: "none",
  minHeight: 152,
  transition: "transform 0.15s ease, box-shadow 0.15s ease",
};

function hoverOn(e)  { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(0,0,0,0.16)"; }
function hoverOff(e) { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "none"; }

function CardLabel({ text, color }) {
  return (
    <p style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color, fontWeight: 600, marginBottom: 6, margin: "0 0 6px" }}>
      {text}
    </p>
  );
}

function CardTitle({ text, color = "#fff" }) {
  return (
    <p style={{ fontSize: 21, fontWeight: 700, letterSpacing: "-0.02em", color, margin: "0 0 12px" }}>
      {text}
    </p>
  );
}

function CardDesc({ text, color = "rgba(255,255,255,0.65)" }) {
  return (
    <p style={{ fontSize: 12, lineHeight: 1.55, color, margin: 0 }}>
      {text}
    </p>
  );
}

/* ── Socials tile ── */
function SocialsTile() {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{ ...cardBase, background: "#18181B", cursor: "pointer" }}
      onClick={() => setOpen(o => !o)}
      onMouseEnter={e => { if (!open) hoverOn(e); }}
      onMouseLeave={hoverOff}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <CardLabel text="Connect" color="#6B7280" />
          <CardTitle text="Socials" />
        </div>
        {open && <X size={14} style={{ color: "#6B7280", marginTop: 2, flexShrink: 0 }} />}
      </div>

      {open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {SOCIALS.map(({ label, href, Icon, color }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 12px", borderRadius: 9,
                background: "#27272A", color: "#fff",
                textDecoration: "none", fontSize: 13, fontWeight: 500,
                transition: "background 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#3F3F46"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#27272A"; }}
            >
              <Icon size={15} style={{ color, flexShrink: 0 }} />
              {label}
            </a>
          ))}
        </div>
      ) : (
        <CardDesc text="Twitter · YouTube · LinkedIn" color="rgba(255,255,255,0.4)" />
      )}
    </div>
  );
}

/* ── Hub page ── */
export default function HubPage() {
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.title = "Malicke Case";
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#F5F3EF",
      fontFamily: "'Inter', system-ui, sans-serif",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "48px 20px",
    }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 44 }}>
        <p style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#5A6470", marginBottom: 10, fontWeight: 500 }}>
          Browns Town, Jamaica
        </p>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 56px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#0E3A3A", margin: 0 }}>
          Malicke <span style={{ color: "#FF6B35" }}>Case</span>
        </h1>
        <p style={{ fontSize: 14, color: "#5A6470", marginTop: 10, fontWeight: 400 }}>
          Engineer · Builder · Writer
        </p>
      </div>

      {/* Card grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 195px), 1fr))",
        gap: 10,
        width: "100%",
        maxWidth: 880,
      }}>

        {/* Forma  -  featured, full width; opens the Forma study platform */}
        <a
          href={FORMA_URL}
          style={{ ...cardBase, gridColumn: "1 / -1", minHeight: 0, background: "#F3EFE6", color: "#1B1F24", border: "1.5px solid #1B1F24", flexDirection: "row", alignItems: "center", gap: 20, flexWrap: "wrap" }}
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
        >
          <div style={{ flex: "1 1 300px" }}>
            <CardLabel text="Study platform · New" color="#2B47C9" />
            <CardTitle text="Forma" color="#1B1F24" />
            <CardDesc text="Shape how you understand." color="rgba(27,31,36,0.7)" />
          </div>
          <span style={{ flexShrink: 0, padding: "10px 16px", borderRadius: 10, background: "#1B1F24", color: "#F3EFE6", fontSize: 13, fontWeight: 600 }}>
            Enter Forma →
          </span>
        </a>

        {/* Portfolio */}
        <Link to="/portfolio" style={{ ...cardBase, background: "#0E3A3A", color: "#fff" }} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
          <CardLabel text="Engineering & Software" color="#FF6B35" />
          <CardTitle text="Portfolio" />
          <CardDesc text="Systems, hardware, and full-stack builds." />
        </Link>

        {/* Blog */}
        <Link to="/blog" style={{ ...cardBase, background: "#FAF7F2", color: "#1A1F26" }} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
          <CardLabel text="Writing & Ideas" color="#0E3A3A" />
          <CardTitle text="Blog" color="#1A1F26" />
          <CardDesc text="Finance, tech, engineering, and life." color="rgba(26,31,38,0.6)" />
        </Link>

        {/* Services are temporarily offline */}
        <Link to="/services" style={{ ...cardBase, background: "#FF6B35", color: "#fff" }} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
          <CardLabel text="Home Projects" color="rgba(255,255,255,0.85)" />
          <CardTitle text="Services" />
          <CardDesc text="Temporarily offline while this page is being updated." />
        </Link>

        {/* Listings preview */}
        <Link to="/listings" style={{ ...cardBase, background: "#072525", color: "#fff" }} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
          <CardLabel text="Properties" color="#FF6B35" />
          <CardTitle text="Listings" />
          <CardDesc text="Coming soon. Sample apartments in St. Ann and Kingston." />
        </Link>

        {/* IMMBRAIN */}
        <Link to="/immbrain" style={{ ...cardBase, background: "#1A1F26", color: "#fff" }} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
          <CardLabel text="Product" color="#60a5fa" />
          <CardTitle text="IMMBRAIN" />
          <CardDesc text="Immigration intelligence platform." />
        </Link>

        {/* Tech Education  -  greyed out, unclickable */}
        <div style={{ ...cardBase, background: "#1C1C1E", opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" }}>
          <CardLabel text="YouTube" color="#FF0000" />
          <CardTitle text="Tech Education" />
          <CardDesc text="Coming soon. Short, practical videos about staying safer online." />
        </div>

        {/* Downloads */}
        <Link to="/downloads" style={{ ...cardBase, background: "#2D3139", color: "#fff" }} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
          <CardLabel text="Apps & Releases" color="#D1D5DB" />
          <CardTitle text="Downloads" />
          <CardDesc text="Desktop software and tools." />
        </Link>

        {/* Socials */}
        <SocialsTile />

      </div>

      <p style={{ marginTop: 36, fontSize: 11, color: "#9CA3AF", letterSpacing: "0.06em" }}>
        malickecase.com
      </p>
    </div>
  );
}
